import React, { useRef, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";

/**
 * Tooltip for the status cell in the sequence contacts table. Renders into
 * document.body via portal + position: fixed so it escapes the table's
 * `overflow: scroll` ancestor (.tableWrapper). That's why we can't reuse
 * the shared CustomTooltip here — that one uses position: absolute, which
 * gets clipped no matter how we tune offsets.
 *
 * Inline top/left styles are computed from the trigger's getBoundingClientRect.
 * That's positioning *data*, not a styling decision — it's the only way to
 * pin a portaled element to a moving trigger. All visual styling lives in
 * SCSS (`.statusCellTooltip*`).
 */
// `interactive` enables click-through inside the tooltip body — needed when
// the tooltip contains links / "Show more" actions (e.g. FAILED reasons).
// Without it, pointer-events:none on the tooltip blocks clicks. Interactive
// mode also adds a small grace period when the cursor leaves the trigger so
// the user has time to move into the tooltip body before it hides.
const StatusCellTooltip = ({
  text,
  children,
  maxWidth = 260,
  gap = 10,
  interactive = false
}) => {
  const triggerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "below" });

  const recalc = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const centerX = r.left + r.width / 2;

    // Default below the trigger; flip to above only if there's no room below
    // AND there's room above. Keeps behavior predictable for most rows.
    const spaceBelow = window.innerHeight - r.bottom;
    const placement = spaceBelow < 80 && r.top > 80 ? "above" : "below";
    const top = placement === "below" ? r.bottom + gap : r.top - gap;
    setPos({ top, left: centerX, placement });
  }, [gap]);

  // Recompute if the user scrolls or resizes while hovering.
  useEffect(() => {
    if (!show) return;
    window.addEventListener("scroll", recalc, true);
    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("scroll", recalc, true);
      window.removeEventListener("resize", recalc);
    };
  }, [show, recalc]);

  // Clear any pending hide on unmount.
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const cancelPendingHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleEnter = () => {
    cancelPendingHide();
    recalc();
    setShow(true);
  };
  const handleLeave = () => {
    if (interactive) {
      // Grace period so the cursor can travel from trigger → tooltip body
      // without the tooltip vanishing mid-flight.
      hideTimerRef.current = setTimeout(() => setShow(false), 120);
    } else {
      setShow(false);
    }
  };

  return (
    <span
      ref={triggerRef}
      className="statusCellTooltipTrigger"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {show &&
        ReactDOM.createPortal(
          <div
            className={`statusCellTooltip statusCellTooltip--${pos.placement} ${
              interactive ? "statusCellTooltip--interactive" : ""
            }`}
            style={{ top: pos.top, left: pos.left, maxWidth }}
            role="tooltip"
            onMouseEnter={interactive ? cancelPendingHide : undefined}
            onMouseLeave={interactive ? handleLeave : undefined}
          >
            <div className="statusCellTooltipArrow" />
            <div className="statusCellTooltipBody">{text}</div>
          </div>,
          document.body
        )}
    </span>
  );
};

export default StatusCellTooltip;
